'use client';

import { useState } from 'react';

import { useContactPreferenceUpdate } from '@sola/my-sdk/settings/hooks.ts';
import type {
  ContactDoNotUpdate,
  ContactInterestUpdate,
} from '@sola/schema/dotcom/contacts/update.ts';
import type { GraphQLContactPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useSupporterContacts } from '@sola/supporter-sdk/hooks/settings.ts';

import type { ContactPreferenceCategory } from '../../molecules/contact-preferences-form/contact-preferences-form.config.ts';
import {
  ContactPreferenceChannel,
  type ContactPreferencesFormValues,
  type OptOutChannels,
  contactPreferenceChannels,
} from '../../molecules/contact-preferences-form/contact-preferences-form.config.ts';
import {
  buildContactPreferencesInitialSelections,
  buildFormDefaultValues as buildSharedFormDefaultValues,
} from '../../molecules/contact-preferences-form/contact-preferences-form.logic.ts';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { createContactPreferenceUpdateSuccessAlert } from '../../utils/alerts.tsx';

export {
  ContactPreferenceCategory,
  ContactPreferenceChannel,
  ContactPreferenceChannel as ContactPreferenceType,
  type ContactPreferencesFormValues,
  type ContactPreferencesState,
  type OptOutChannels,
  contactPreferenceChannels,
} from '../../molecules/contact-preferences-form/contact-preferences-form.config.ts';
export {
  buildContactPreferencesInitialSelections,
  buildPreferencePayload,
  buildReactivationUpdates,
} from '../../molecules/contact-preferences-form/contact-preferences-form.logic.ts';

/** Compatibility name retained for existing sheet consumers and tests. */
export type OptOutPreferenceTypes = OptOutChannels;

const emptyOptOutChannels: OptOutChannels = new Set();

/**
 * Converts the API preference shape directly into the form's default values.
 * The reusable form logic works from normalized selections, so this adapter
 * keeps the sheet's existing API while sharing the mapping implementation.
 */
export const buildFormDefaultValues = (
  currentPreferences?: GraphQLContactPreference | null,
  optOutChannels: OptOutChannels = emptyOptOutChannels,
): ContactPreferencesFormValues =>
  buildSharedFormDefaultValues(
    buildContactPreferencesInitialSelections(currentPreferences),
    optOutChannels,
  );

export interface ChannelOptionInput {
  id: string;
  label: string;
  value: string;
}

export interface PreferenceRowDescriptor {
  preference: ContactPreferenceCategory;
  options: (ChannelOptionInput & {
    disabled: boolean;
    disabledTooltipText?: string;
  })[];
}

export interface BuildPreferenceRowsParams {
  channelOptions: Record<ContactPreferenceChannel, ChannelOptionInput>;
  channelOptOutTooltips: Record<ContactPreferenceChannel, string>;
  optOutPreferenceTypes?: OptOutChannels;
}

/** Builds serializable row descriptors from the shared preference config. */
export const buildPreferenceRows = ({
  channelOptions,
  channelOptOutTooltips,
  optOutPreferenceTypes = emptyOptOutChannels,
}: BuildPreferenceRowsParams): PreferenceRowDescriptor[] =>
  (
    Object.entries(contactPreferenceChannels) as [
      ContactPreferenceCategory,
      (typeof contactPreferenceChannels)[ContactPreferenceCategory],
    ][]
  ).map(
    ([preference, { channels, disabledChannels, alwaysCheckedChannels }]) => ({
      preference,
      options: channels.map((channel) => {
        const isAlwaysChecked =
          alwaysCheckedChannels?.includes(channel) ?? false;
        const showsOptOutState =
          optOutPreferenceTypes.has(channel) && !isAlwaysChecked;

        return {
          ...channelOptions[channel],
          disabled:
            (disabledChannels?.includes(channel) ?? false) || showsOptOutState,
          disabledTooltipText: showsOptOutState
            ? channelOptOutTooltips[channel]
            : undefined,
        };
      }),
    }),
  );

export interface ContactPreferenceUpdateParams {
  contactId: string;
  preferences:
    | ContactInterestUpdate['preferences']
    | ContactDoNotUpdate['preferences'];
  name: string;
}

/** Submits the preferences mutation and shows a success alert on success. */
export const useContactPreferenceUpdateData = () => {
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const { mutateAsync: updatePreferences, isPending } =
    useContactPreferenceUpdate();

  const submitPreferenceUpdate = async ({
    contactId,
    preferences,
    name,
  }: ContactPreferenceUpdateParams): Promise<boolean> => {
    clearAlerts();
    try {
      await updatePreferences({ contactId, preferences });
      addAlerts(createContactPreferenceUpdateSuccessAlert(name));
      return true;
    } catch {
      return false;
    }
  };

  return {
    submitPreferenceUpdate,
    isPending,
  };
};

export interface UseContactPreferencesSheetProps {
  contactId: string;
}

export const useContactPreferencesSheet = ({
  contactId,
}: UseContactPreferencesSheetProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { contacts } = useSupporterContacts();
  const { submitPreferenceUpdate, isPending } =
    useContactPreferenceUpdateData();

  const contact = contacts?.find(({ id }) => id === contactId);
  const currentPreferences = contact?.preferences;

  const optOutPreferenceTypes: OptOutChannels = new Set([
    ...(currentPreferences?.doNotEmail ? [ContactPreferenceChannel.Email] : []),
    ...(currentPreferences?.doNotText ? [ContactPreferenceChannel.Text] : []),
  ]);

  const formDefaultValues = buildFormDefaultValues(
    currentPreferences,
    optOutPreferenceTypes,
  );
  const supporterName = contact?.firstName ?? contact?.lastName ?? '';

  return {
    isOpen,
    setIsOpen,
    formDefaultValues,
    currentPreferences,
    optOutPreferenceTypes,
    supporterName,
    isSaving: isPending,
    submitPreferenceUpdate,
  };
};
